import type {
  ApplicationStatus,
  FeePayer,
  Payout,
  PayoutApplication,
  PayoutFailureDetail,
  PayoutStatus,
  ReceiptView,
} from '@/api/payouts';

/**
 * 「一笔钱」的完整视图 —— 打款申请页与打款记录页的详情抽屉共用这一种形状。
 *
 * 为什么要归一化：这两页看的是同一件事的两半。申请回答「为什么要打这笔钱、谁批的」，
 * 打款记录回答「钱怎么走的、到了没有」。人在任何一页点开一条记录，想知道的都是
 * 全部两半 —— 而它们原本分在两个列表里，只能靠短 id 横跨两页手动搜一遍。
 *
 * 🔴 **只做搬运，不做推导。** 尤其不要从打款记录的状态反推申请状态：那等于在前端
 * 重写一遍 §8.2 的状态机，两处一旦走偏，同一笔钱会在两页显示成两回事。
 * 拿不到的字段就是 null，界面上如实留空。
 */
export interface PayoutDetail {
  payeeId: string;
  payeeName: string | null;
  payeeNickname: string | null;
  payeeShortId: string;
  amount: string;
  currency: string;
  application: {
    id: string;
    /**
     * 申请状态。🔴 从打款记录那一侧进来时是 **null** —— 那个接口没带它，
     * 而「PROCESSING 的打款对应 PROCESSING 的申请」这种推导正是上面禁止的事。
     */
    status: ApplicationStatus | null;
    reference: string;
    note: string | null;
    createdByName: string | null;
    createdAt: string;
    /** 承担方在创建申请时定死、之后永不改写（§5.8），所以两侧取到的必然是同一个值。 */
    feePaidBy: FeePayer | null;
    swiftChargeOption: string | null;
    rejectionReason: string | null;
    rejectedByName: string | null;
  };
  /** 还没执行过打款时为 null。 */
  payout: {
    id: string;
    status: PayoutStatus;
    providerStatus: string | null;
    shortReferenceId: string | null;
    failureReason: string | null;
    failureDetail: PayoutFailureDetail | null;
    receipt: ReceiptView;
    executedByName: string | null;
    createdAt: string;
    paidAt: string | null;
    /** 🔴 `amount` 为 null = 上游还没给出费用结果，不是「不收费」。 */
    fee: { amount: string | null; beneficiaryReceives: string | null };
  } | null;
}

/** 打款申请页的一行 → 详情。 */
export function detailFromApplication(row: PayoutApplication): PayoutDetail {
  return {
    payeeId: row.payeeId,
    payeeName: row.payeeName,
    payeeNickname: row.payeeNickname,
    payeeShortId: row.payeeShortId,
    amount: row.amount,
    currency: row.currency,
    application: {
      id: row.id,
      status: row.status,
      reference: row.reference,
      note: row.note,
      createdByName: row.createdByName,
      createdAt: row.createdAt,
      feePaidBy: row.feePaidBy,
      swiftChargeOption: row.swiftChargeOption,
      rejectionReason: row.rejectionReason,
      rejectedByName: row.rejectedByName,
    },
    payout: row.payout,
  };
}

/** 打款记录页的一行 → 详情。 */
export function detailFromPayout(row: Payout): PayoutDetail {
  return {
    payeeId: row.payeeId,
    payeeName: row.payeeName,
    payeeNickname: row.payeeNickname,
    payeeShortId: row.payeeShortId,
    amount: row.amount,
    currency: row.currency,
    application: {
      id: row.applicationId,
      // 🔴 不推导。这个接口没带申请状态，就如实留空。
      status: null,
      reference: row.reference,
      note: row.note,
      createdByName: row.applicationCreatedByName,
      createdAt: row.applicationCreatedAt,
      feePaidBy: row.fee.paidBy,
      swiftChargeOption: row.fee.swiftChargeOption,
      // 一条已经打过款的申请不可能是 REJECTED（§8.2 单向推进），所以这里恒为空
      rejectionReason: null,
      rejectedByName: null,
    },
    payout: {
      id: row.id,
      status: row.status,
      providerStatus: row.providerStatus,
      shortReferenceId: row.shortReferenceId,
      failureReason: row.failureReason,
      failureDetail: row.failureDetail,
      receipt: row.receipt,
      executedByName: row.executedByName,
      createdAt: row.createdAt,
      paidAt: row.paidAt,
      fee: { amount: row.fee.amount, beneficiaryReceives: row.fee.beneficiaryReceives },
    },
  };
}

/**
 * 抽屉顶部要不要挂一条醒目提示，以及说什么。
 *
 * 🔴 这几句话的措辞是这个抽屉里最要紧的东西。详情页是人**决定下一步做什么**的地方，
 * 一句「打款失败」会直接把人推去重打一笔可能已经在路上的钱（§5.2）。所以：
 *
 * - `INITIATING` / `UNKNOWN` 说的是「结果未知」，且明确写「不要重新发起」；
 * - 「到账后被退回」要说清它曾经成功过 —— 财务多半已经按到账处理了后续（§5.5.2）。
 *
 * 返回 null 表示这条记录没什么要特别提醒的（待打款、处理中、已到账、确定失败）。
 */
export interface DetailNotice {
  type: 'warning' | 'error';
  title: string;
  description: string;
}

export function detailNotice(payout: PayoutDetail['payout']): DetailNotice | null {
  if (!payout) return null;
  if (payout.status === 'CANCELLED' && payout.paidAt) {
    return {
      type: 'error',
      title: '这笔钱到账后被退回',
      description:
        '它曾经打成功，之后被收款行退票或收款人要求退回，钱已回到 Airwallex 账户。如果当时已按到账处理过后续（通知对方、记账），需要回头处理。要再付得新建一条申请。',
    };
  }
  if (payout.status === 'INITIATING') {
    return {
      type: 'warning',
      title: '已发起，结果还没确认',
      description:
        '请求已经发出，但还没收到 Airwallex 的确认 —— 这笔钱可能已经在路上。系统每小时整点自动核对，也可以在列表上单条刷新。请勿重新发起打款。',
    };
  }
  if (payout.status === 'UNKNOWN') {
    return {
      type: 'warning',
      title: '状态未知，需要人工核对',
      description:
        '按请求号在 Airwallex 反查不到这笔转账。反查不到不等于钱没打出去，请先到 Airwallex 后台核对，确认之后再决定要不要新建申请重打。',
    };
  }
  return null;
}
